import { IonContent, IonPage } from '@ionic/react';
import { useClerk, useUser } from '../auth/useAuth';
import React, { useState } from 'react';
import { Btn } from '../components/chancey';
import { safeAreaPadding } from '../components/layout/safeArea';
import { useUserPrefs } from '../hooks/useUserPrefs';
import { useChanceyTheme } from '../theme/ThemeContext';

const AgeIneligiblePage: React.FC = () => {
  const { c, typ, d } = useChanceyTheme();
  const clerk = useClerk();
  const { user } = useUser();
  const { prefs, setPartial } = useUserPrefs();
  const [signingOut, setSigningOut] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [draft, setDraft] = useState(prefs.birthday);
  const [draftError, setDraftError] = useState<string | null>(null);

  const signOut = async () => {
    setSigningOut(true);
    try {
      await clerk.signOut();
    } finally {
      setSigningOut(false);
    }
  };

  const applyCorrection = () => {
    const value = draft;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      setDraftError('Please enter a valid date (YYYY-MM-DD).');
      return;
    }
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed) || parsed > Date.now()) {
      setDraftError('Please enter a valid date.');
      return;
    }
    setPartial({ birthday: value, birthdayImported: true });
    setCorrectOpen(false);
  };

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false} style={{ '--background': c.bg }}>
        <div
          className="flex min-h-full flex-col items-center justify-center gap-5 p-6 text-center"
          style={{ ...safeAreaPadding(24, d.pad, 24, d.pad), background: c.bg }}
        >
          <div
            className="flex h-[72px] w-[72px] items-center justify-center rounded-full text-[34px] text-white"
            style={{
              background: c.gradient,
              fontFamily: typ.display,
              fontWeight: typ.displayWeight,
              letterSpacing: '-0.03em',
            }}
            aria-hidden
          >
            18+
          </div>
          <div
            className="text-[26px] leading-tight"
            style={{
              fontFamily: typ.display,
              fontWeight: typ.displayWeight,
              letterSpacing: typ.displayLetter,
              color: c.ink,
              maxWidth: 320,
            }}
          >
            Chancey is for 18 and over
          </div>
          <p
            className="max-w-[320px] text-[14px] leading-relaxed"
            style={{ color: c.inkSoft, fontFamily: typ.body }}
          >
            Based on the birthday on your account, you are under 18. Per our Terms of Service, we
            are unable to give you access to the app. If this is a mistake, you can correct your
            birthday below or sign out.
          </p>

          {user?.id ? (
            <div
              className="w-full max-w-[320px] rounded-[16px] border p-3 text-left"
              style={{ borderColor: c.line, background: c.surface }}
            >
              <div
                className="text-[11px] font-semibold uppercase tracking-[0.1em]"
                style={{ color: c.inkMuted, fontFamily: typ.body }}
              >
                On file
              </div>
              <div
                className="mt-1 text-sm"
                style={{ color: c.ink, fontFamily: typ.body, fontWeight: 500 }}
              >
                {prefs.birthday || 'Not set'}
              </div>
            </div>
          ) : null}

          <div className="mt-1 flex w-full max-w-[320px] flex-col gap-2">
            <Btn
              c={c}
              typ={typ}
              variant="ghost"
              onClick={() => {
                setDraft(prefs.birthday || '');
                setDraftError(null);
                setCorrectOpen((v) => !v);
              }}
            >
              {correctOpen ? 'Cancel correction' : 'Correct my birthday'}
            </Btn>
            {correctOpen && (
              <div className="flex flex-col gap-2 rounded-[14px] border p-3" style={{ borderColor: c.line, background: c.surface }}>
                <input
                  type="date"
                  value={draft}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDraft(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2 text-[15px] outline-none"
                  style={{ borderColor: c.line, background: c.surfaceAlt, color: c.ink, fontFamily: typ.body }}
                />
                {draftError && (
                  <div className="text-[12px]" style={{ color: c.lucky, fontFamily: typ.body }}>
                    {draftError}
                  </div>
                )}
                <Btn c={c} typ={typ} variant="primary" onClick={applyCorrection}>
                  Save
                </Btn>
              </div>
            )}
            <Btn c={c} typ={typ} variant="primary" onClick={() => void signOut()} disabled={signingOut}>
              {signingOut ? 'Signing out…' : 'Sign out'}
            </Btn>
          </div>

          <a
            href="https://www.ncpgambling.org/help-treatment/helpline/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 text-[11px] underline"
            style={{ color: c.inkMuted, fontFamily: typ.body }}
          >
            National Council on Problem Gambling helpline
          </a>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AgeIneligiblePage;
